import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Globe2, Lock, FolderKanban } from "lucide-react";
import { useWorkbenches, WorkbenchVisibility } from "@/api/workbenches";
import { useAuthStore } from "@/stores/authStore";
import CreateWorkbenchModal from "./CreateWorkbenchModal";

const VISIBILITY_STYLE: Record<WorkbenchVisibility, string> = {
  PUBLICA: "bg-blue-100 text-blue-700",
  PRIVADA: "bg-amber-100 text-amber-700",
};

export default function WorkbenchesPage() {
  const [visibility, setVisibility] = useState<WorkbenchVisibility | "">("");
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const hasPermission = useAuthStore((s) => s.hasPermission);

  const { data, isLoading } = useWorkbenches({ visibility: visibility || undefined, search: search || undefined, pageSize: 30 });
  const workbenches = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Mesa de Trabajo</h2>
          <p className="text-sm text-gray-500">Espacios de equipo, públicos o privados, con Excel y Word integrados.</p>
        </div>
        {hasPermission("workbenches.create") && (
          <button type="button" className="btn-primary flex items-center gap-1.5" onClick={() => setShowCreate(true)}>
            <Plus size={16} /> Nueva mesa
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <input
          className="input-field w-64"
          placeholder="Buscar por nombre..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="input-field w-auto" value={visibility} onChange={(e) => setVisibility(e.target.value as WorkbenchVisibility | "")}>
          <option value="">Todas las visibilidades</option>
          <option value="PUBLICA">Pública</option>
          <option value="PRIVADA">Privada</option>
        </select>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400">Cargando...</p>
      ) : workbenches.length === 0 ? (
        <div className="card flex flex-col items-center gap-2 p-10 text-center text-gray-400">
          <FolderKanban size={32} />
          <p className="text-sm">No hay mesas de trabajo visibles todavía.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {workbenches.map((wb) => (
            <Link key={wb.id} to={`/workbenches/${wb.id}`} className="card block p-4 transition-shadow hover:shadow-md">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-medium text-gray-900">{wb.name}</h3>
                <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${VISIBILITY_STYLE[wb.visibility]}`}>
                  {wb.visibility === "PUBLICA" ? <Globe2 size={12} /> : <Lock size={12} />}
                  {wb.visibility === "PUBLICA" ? "Pública" : "Privada"}
                </span>
              </div>
              {wb.description && <p className="mt-1 line-clamp-2 text-sm text-gray-500">{wb.description}</p>}
              <p className="mt-3 text-xs text-gray-400">Creada por {wb.createdBy?.fullName ?? "—"}</p>
            </Link>
          ))}
        </div>
      )}

      {showCreate && <CreateWorkbenchModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}
